import { Button, Modal } from 'react-bootstrap';
import React, { useState } from 'react';

import { DocumentQuery } from '../../../generated/graphql';
import styled from 'styled-components';

type Attachment = NonNullable<NonNullable<DocumentQuery['document']>['attachments']>[0];
type Attachments = NonNullable<DocumentQuery['document']>['attachments'];
interface AttachmentsProps {
    attachments?: Attachments;
}

const Section = styled.div`
    margin-bottom: 2rem;
`;

const SectionTitle = styled.h5`
    color: #495057;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const FileGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const FileCard = styled.div`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        background: #e9ecef;
        transform: translateY(-2px);
    }
` as React.ComponentType<React.HTMLAttributes<HTMLDivElement>>;

const FileName = styled.div`
    font-size: 0.875rem;
    color: #495057;
    margin-bottom: 0.5rem;
    word-break: break-all;
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
`;

const ImageCard = styled.div`
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 1;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
` as React.ComponentType<React.HTMLAttributes<HTMLDivElement>>;

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
` as React.ComponentType<React.ImgHTMLAttributes<HTMLImageElement>>;

const StyledModal = styled(Modal)`
    .modal-content {
        border-radius: 12px;
        overflow: hidden;
    }

    .modal-header {
        background: linear-gradient(to right, #1a1a1a, #2d2d2d);
        color: white;
        border-bottom: none;
    }

    .modal-footer {
        border-top: none;
        padding: 1rem;
    }
`;

const PreviewContent = styled.div`
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    margin: 1rem;
`;

const PreviewImage = styled.img`
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 8px;
` as React.ComponentType<React.ImgHTMLAttributes<HTMLImageElement>>;

const PreviewIframe = styled.iframe`
    width: 100%;
    height: 70vh;
    border: none;
    border-radius: 8px;
` as React.ComponentType<React.IframeHTMLAttributes<HTMLIFrameElement>>;

const ActionButton = styled(Button)`
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-1px);
    }
` as React.ComponentType<React.ComponentProps<typeof Button>>;

interface FilePreviewProps {
    attachment: Attachment;
    show: boolean;
    onHide: () => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({ attachment, show, onHide }) => {
    if (!attachment.url) return null;

    const fileExtension = attachment.name.split('.').pop()?.toLowerCase();
    let previewContent = null;

    switch (fileExtension) {
        case 'pdf':
            previewContent = <PreviewIframe src={attachment.url} title={attachment.name} />;
            break;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            previewContent = <PreviewImage src={attachment.url} alt={attachment.name} />;
            break;
        case 'doc':
        case 'docx':
        case 'xls':
        case 'xlsx':
        case 'ppt':
        case 'pptx':
            previewContent = (
                <PreviewIframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(attachment.url)}`}
                    title={attachment.name}
                />
            );
            break;
        default:
            previewContent = (
                <div className="text-center p-5">
                    <p>Preview not available for this file type</p>
                    <ActionButton as="a" href={attachment.url} target="_blank" download variant="primary">
                        Download File
                    </ActionButton>
                </div>
            );
    }

    return (
        <StyledModal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{attachment.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <PreviewContent>{previewContent}</PreviewContent>
            </Modal.Body>
            <Modal.Footer>
                <ActionButton variant="secondary" onClick={onHide}>
                    Close
                </ActionButton>
                <ActionButton as="a" href={attachment.url} target="_blank" download variant="primary">
                    Download
                </ActionButton>
            </Modal.Footer>
        </StyledModal>
    );
};

export const Attachments: React.FC<AttachmentsProps> = ({ attachments }) => {
    const [selectedFile, setSelectedFile] = useState<Attachment | null>(null);
    const images: Attachments = [];
    const files: Attachments = [];

    attachments?.forEach((attachment) => {
        if (attachment.name.endsWith('jpg') || attachment.name.endsWith('gif') || attachment.name.endsWith('png')) {
            return images.push(attachment);
        }
        return files.push(attachment);
    });

    return (
        <>
            <Section>
                <SectionTitle>Files ({files?.length || 0})</SectionTitle>
                <FileGrid>
                    {files?.map((attachment) => {
                        if (!attachment.url) return null;
                        return (
                            <FileCard key={attachment.id} onClick={() => setSelectedFile(attachment)}>
                                <FileName>{attachment.name}</FileName>
                                <ActionButton variant="outline-primary" size="sm" block>
                                    View File
                                </ActionButton>
                            </FileCard>
                        );
                    })}
                </FileGrid>
            </Section>

            {images.length > 0 && (
                <Section>
                    <SectionTitle>Images ({images?.length || 0})</SectionTitle>
                    <ImageGrid>
                        {images?.map((attachment) => {
                            if (!attachment.url) return null;
                            return (
                                <ImageCard key={attachment.id} onClick={() => setSelectedFile(attachment)}>
                                    <Image src={attachment.url} alt={attachment.name} />
                                </ImageCard>
                            );
                        })}
                    </ImageGrid>
                </Section>
            )}

            {selectedFile && (
                <FilePreview attachment={selectedFile} show={!!selectedFile} onHide={() => setSelectedFile(null)} />
            )}
        </>
    );
};
